import { useCallback, useEffect, useState } from "react";
import { Mail } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { microsoftMailApi, type MicrosoftMailStatus } from "../../backend/api/microsoftMail";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";

export default function MicrosoftMailConnection() {
  const [status, setStatus] = useState<MicrosoftMailStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [search] = useSearchParams();
  const outcome = search.get("email");
  const load = useCallback(async () => {
    setLoading(true);
    const result = await microsoftMailApi.status();
    setLoading(false);
    if (result.error) { setError(result.error.message); return; }
    setStatus(result.data);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const connect = async () => {
    setBusy(true);
    setError("");
    const result = await microsoftMailApi.connect();
    if (result.error || !result.data) { setError(result.error?.message || "Microsoft connection could not be started."); setBusy(false); return; }
    window.location.assign(result.data.authorize_url);
  };
  const disconnect = async () => {
    setBusy(true);
    setError("");
    const result = await microsoftMailApi.disconnect();
    if (result.error) { setError(result.error.message); }
    else { await load(); }
    setBusy(false);
  };
  const connection = status?.connection;
  return (
    <Card>
      <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-muted">
            <Mail className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <CardTitle className="text-base">Microsoft 365 email</CardTitle>
            <CardDescription>
              {loading ? "Checking connection..." : connection
                ? `${connection.mailbox} · ${status?.delivery_enabled ? "Invitations and password resets enabled" : "Reconnect to resume email delivery"}`
                : "Connect a company mailbox to send user invitations and password resets."}
            </CardDescription>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={loading || busy || !status?.configured} onClick={() => void connect()}>
            {busy ? "Working..." : connection ? "Reconnect" : "Connect Microsoft 365"}
          </Button>
          {connection && <Button variant="outline" disabled={busy} onClick={() => void disconnect()}>Disconnect</Button>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!loading && status && !status.configured && <p className="text-sm text-muted-foreground">Microsoft 365 app setup is required before you can connect a mailbox. Ask your administrator to configure the application credentials.</p>}
        <p className="text-sm text-muted-foreground">Sign in with the mailbox you want the portal to send from. Microsoft may require approval from your organisation’s administrator. Disconnecting stops future invitation and password-reset emails.</p>
        {(error || outcome === "failed" || outcome === "cancelled") && <Alert variant={outcome === "cancelled" && !error ? "default" : "destructive"}>
          <AlertTitle>{outcome === "cancelled" && !error ? "Connection cancelled" : "Email connection needs attention"}</AlertTitle>
          <AlertDescription>{error || (outcome === "cancelled" ? "Your existing connection has been kept." : "Microsoft 365 could not be connected. Check the application setup and Microsoft permissions, then try again.")}</AlertDescription>
        </Alert>}
        {error && <Button variant="outline" disabled={loading || busy} onClick={() => { setError(""); void load(); }}>Retry</Button>}
      </CardContent>
    </Card>
  );
}
